import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  listStreamCameras,
  listStreams,
  startStream,
  stopAllStreams,
  stopStream,
  type NvrCamera,
  type StreamCamera,
  type StreamsResponse,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';

const POLL_INTERVAL_MS = 5_000;
const KEYLESS_TOOLTIP = 'No stream key is configured for this channel on the hall NUC.';

interface ActionError {
  channel: number;
  message: string;
  lastLog: string | null;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function StreamsPage() {
  const { isAdmin } = useAuth();
  const { push } = useToast();
  const [streams, setStreams] = useState<StreamsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyChannels, setBusyChannels] = useState<Set<number>>(() => new Set());
  const [stoppingEverything, setStoppingEverything] = useState(false);
  const [stopEverythingOpen, setStopEverythingOpen] = useState(false);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [nvrCameras, setNvrCameras] = useState<NvrCamera[] | null>(null);
  const [nvrLoading, setNvrLoading] = useState(false);
  const [nvrError, setNvrError] = useState<string | null>(null);
  const statusController = useRef<AbortController | null>(null);
  const actionStatusControllers = useRef(new Set<AbortController>());
  const latestStatusRequest = useRef(0);
  const nvrController = useRef<AbortController | null>(null);
  const stopEverythingPending = useRef(false);
  const cameraActionsPending = useRef(new Set<number>());

  const loadStreams = useCallback(async (source: 'poll' | 'action' = 'poll'): Promise<StreamsResponse | null> => {
    if (source === 'poll' && actionStatusControllers.current.size > 0) return null;
    if (source === 'poll') statusController.current?.abort();
    const controller = new AbortController();
    const requestId = ++latestStatusRequest.current;
    if (source === 'poll') statusController.current = controller;
    else actionStatusControllers.current.add(controller);
    try {
      const next = await listStreams(controller.signal);
      if (!controller.signal.aborted) {
        if (requestId === latestStatusRequest.current) {
          setStreams(next);
          setLoadError(null);
        }
        return next;
      }
    } catch (error) {
      if (!controller.signal.aborted && requestId === latestStatusRequest.current) {
        setLoadError(errorMessage(error, 'Could not load stream status.'));
      }
      return null;
    } finally {
      if (!controller.signal.aborted && requestId === latestStatusRequest.current) setLoading(false);
      if (statusController.current === controller) statusController.current = null;
      actionStatusControllers.current.delete(controller);
    }
    return null;
  }, []);

  useEffect(() => {
    if (isAdmin !== true) return undefined;
    const actionControllers = actionStatusControllers.current;
    void loadStreams();
    const interval = window.setInterval(() => { void loadStreams(); }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      statusController.current?.abort();
      actionControllers.forEach((controller) => controller.abort());
      actionControllers.clear();
      nvrController.current?.abort();
    };
  }, [isAdmin, loadStreams]);

  const runCameraAction = async (camera: StreamCamera, action: 'start' | 'stop') => {
    if (cameraActionsPending.current.has(camera.channel)) return;
    cameraActionsPending.current.add(camera.channel);
    setBusyChannels((current) => new Set(current).add(camera.channel));
    setActionError(null);
    try {
      if (action === 'start') await startStream(camera.channel);
      else await stopStream(camera.channel);
      // Do not optimistic-update from the action request. A fresh controller
      // read is the source of truth when two admins (or the NUC) act at once.
      await loadStreams('action');
    } catch (error) {
      const message = errorMessage(error, `Could not ${action} stream.`);
      const observed = await loadStreams('action');
      const observedCamera = observed?.cameras.find((entry) => entry.channel === camera.channel);
      setActionError({ channel: camera.channel, message, lastLog: observedCamera?.lastLog ?? camera.lastLog ?? null });
      push('error', message);
    } finally {
      cameraActionsPending.current.delete(camera.channel);
      setBusyChannels((current) => {
        const next = new Set(current);
        next.delete(camera.channel);
        return next;
      });
    }
  };

  const confirmStopEverything = async () => {
    if (stopEverythingPending.current) return;
    stopEverythingPending.current = true;
    setStoppingEverything(true);
    setActionError(null);
    try {
      await stopAllStreams();
      setStopEverythingOpen(false);
      await loadStreams('action');
    } catch (error) {
      const message = errorMessage(error, 'Could not stop all streams.');
      setActionError({ channel: -1, message, lastLog: null });
      push('error', message);
      setStopEverythingOpen(false);
    } finally {
      setStoppingEverything(false);
      stopEverythingPending.current = false;
    }
  };

  const lookupNvrCameras = async () => {
    nvrController.current?.abort();
    const controller = new AbortController();
    nvrController.current = controller;
    setNvrLoading(true);
    setNvrError(null);
    try {
      const result = await listStreamCameras(controller.signal);
      if (!controller.signal.aborted) setNvrCameras(result.cameras);
    } catch (error) {
      if (!controller.signal.aborted) setNvrError(errorMessage(error, 'Could not list NVR channels.'));
    } finally {
      if (!controller.signal.aborted) setNvrLoading(false);
      if (nvrController.current === controller) nvrController.current = null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1120 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Streams</h1>
        <p style={{ ...T.ui, color: PS.textSec, margin: '6px 0 0' }}>
          Start and stop the hall camera streams. Status refreshes every five seconds.
        </p>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Button
          variant="danger"
          onClick={() => setStopEverythingOpen(true)}
          disabled={stoppingEverything || loading || streams === null}
        >
          Stop everything
        </Button>
        <Button variant="secondary" onClick={() => void lookupNvrCameras()} disabled={nvrLoading}>
          {nvrLoading ? 'Checking NVR channels…' : 'Check NVR channels'}
        </Button>
        {streams && (
          <span style={{ ...T.caption, color: streams.nvr.ok ? PS.success : PS.warning }}>
            NVR {streams.nvr.ok ? 'reachable' : 'unreachable'}{streams.nvr.host ? ` · ${streams.nvr.host}` : ''}
          </span>
        )}
      </div>

      {loading && <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>Loading stream status…</p>}
      {loadError && (
        <div role="alert" style={errorStyle}>
          {loadError}{' '}
          <button onClick={() => void loadStreams()} style={retryStyle}>Retry</button>
        </div>
      )}

      {streams && (
        <div style={{ overflowX: 'auto', background: PS.white, border: `1px solid ${PS.border}`, borderRadius: 4 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Camera</th>
                <th style={thStyle}>Channel</th>
                <th style={thStyle}>State</th>
                <th style={thStyle}>Controls</th>
              </tr>
            </thead>
            <tbody>
              {streams.cameras.map((camera) => {
                const error = actionError?.channel === camera.channel ? actionError : null;
                const isBusy = busyChannels.has(camera.channel);
                return (
                  <tr key={camera.channel}>
                    <td style={tdStyle}>{camera.name}</td>
                    <td style={tdStyle}>{camera.channel}</td>
                    <td style={tdStyle} aria-label={camera.state}>
                      <span style={stateStyle(camera.state)}>{camera.state}</span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Button
                          onClick={() => void runCameraAction(camera, 'start')}
                          disabled={!camera.hasKey || isBusy}
                          title={!camera.hasKey ? KEYLESS_TOOLTIP : undefined}
                          aria-label={`Go Live ${camera.name}`}
                        >
                          {isBusy ? 'Working…' : 'Go Live'}
                        </Button>
                        {!camera.hasKey && (
                          <span style={{ ...T.caption, color: PS.textSec }}>
                            No stream key configured on the hall NUC.
                          </span>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => void runCameraAction(camera, 'stop')}
                          disabled={isBusy}
                          aria-label={`Stop ${camera.name}`}
                        >
                          {isBusy ? 'Working…' : 'Stop'}
                        </Button>
                      </div>
                      {error && (
                        <div role="alert" style={{ ...rowErrorStyle, marginTop: 8 }}>
                          <div>{error.message}</div>
                          <div>Last device log: {error.lastLog ?? 'No log line reported.'}</div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section aria-labelledby="nvr-channels-heading" style={nvrPanelStyle}>
        <h2 id="nvr-channels-heading" style={{ ...T.subheading, margin: 0 }}>NVR channels</h2>
        {nvrLoading && <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>Looking up NVR channels…</p>}
        {nvrError && <p role="alert" style={{ ...T.ui, color: PS.danger, margin: 0 }}>{nvrError}</p>}
        {!nvrLoading && !nvrError && nvrCameras === null && (
          <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>Check the NVR only when you need its complete channel list; it can take a little while.</p>
        )}
        {nvrCameras && (
          nvrCameras.length === 0
            ? <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>No NVR channels found.</p>
            : <ul style={{ margin: 0, paddingLeft: 20, ...T.ui }}>
              {nvrCameras.map((camera) => <li key={camera.channel}>{camera.name} (channel {camera.channel})</li>)}
            </ul>
        )}
      </section>

      {actionError?.channel === -1 && <div role="alert" style={errorStyle}>{actionError.message}</div>}

      <ConfirmDialog
        open={stopEverythingOpen}
        title="Stop every stream?"
        message="This sends one stop command to the hall stream controller and takes every camera off air."
        confirmLabel="Stop everything"
        destructive
        confirmDisabled={stoppingEverything}
        onConfirm={confirmStopEverything}
        onCancel={() => setStopEverythingOpen(false)}
      />
    </div>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse' as const, minWidth: 680 };
const thStyle = {
  ...T.label,
  color: PS.textSec,
  background: PS.pageSecondary,
  padding: '12px 16px',
  borderBottom: `1px solid ${PS.border}`,
  textAlign: 'left' as const,
};
const tdStyle = { ...T.ui, padding: '14px 16px', borderBottom: `1px solid ${PS.borderLight}`, verticalAlign: 'top' as const };
const errorStyle = { ...T.ui, color: PS.errorText, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`, borderRadius: 4, padding: '12px 16px' };
const rowErrorStyle = { ...T.caption, color: PS.errorText, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`, borderRadius: 3, padding: 8 };
const retryStyle = { ...T.ui, color: PS.linkBlue, background: 'transparent', border: 0, cursor: 'pointer', padding: 0, textDecoration: 'underline' };
const nvrPanelStyle = { display: 'flex', flexDirection: 'column' as const, gap: 8, background: PS.white, border: `1px solid ${PS.border}`, borderRadius: 4, padding: 16 };

function stateStyle(state: StreamCamera['state']) {
  const color = state === 'live' ? PS.success : state === 'preview' ? PS.info : PS.textSec;
  return { ...T.caption, color, border: `1px solid ${color}`, borderRadius: 999, padding: '3px 8px' };
}
